import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import api from '../../utils/api';

const CheckoutSuccessPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { completeLoginSession } = useAuth();

  const seo = (
    <Helmet>
      <title>Checkout Success | Receevit®</title>
      <meta name="description" content="Checkout complete. Receevit® is finalising your account and logging you in securely." />
      <link rel="canonical" href="https://www.receevit.com/checkout-success" />
      <meta property="og:title" content="Checkout Success | Receevit®" />
      <meta property="og:description" content="Checkout complete. Receevit® is finalising your account and logging you in securely." />
      <meta property="og:url" content="https://www.receevit.com/checkout-success" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://www.receevit.com/og-image.png" />
    </Helmet>
  );

  useEffect(() => {
    const handleCheckoutSuccess = async () => {
      if (!sessionId) {
        setError('No session ID found. Please contact support if you completed payment.');
        setLoading(false);
        return;
      }

      try {
        console.log('Processing checkout success for session:', sessionId);
        
        // Call backend to verify session and get login token
        const response = await api.post('/api/auth/checkout-success', {
          session_id: sessionId
        });

        if (response.data.success && response.data.token && response.data.user) {
          console.log('Checkout verified, auto-logging in user...');
          
          // Use AuthContext to complete login session
          try {
            await completeLoginSession({
              token: response.data.token,
              userData: response.data.user,
              loginEmail: response.data.user.email
            });
            
            console.log('✅ User logged in successfully, redirecting to dashboard...');
            // Redirect to dashboard after successful login
            navigate('/portal/dashboard', { replace: true });
          } catch (loginError) {
            console.error('Failed to complete login session:', loginError);
            setError('Failed to complete login. Please try logging in manually.');
            setLoading(false);
          }
        } else {
          setError(response.data.error || 'Failed to verify checkout session. Please try logging in manually.');
          setLoading(false);
        }
      } catch (err) {
        console.error('Checkout success error:', err);
        const errorMessage = err.response?.data?.error || 
                           err.message || 
                           'Failed to complete checkout. Please try logging in manually.';
        setError(errorMessage);
        setLoading(false);
      }
    };

    handleCheckoutSuccess();
  }, [sessionId, navigate, completeLoginSession]);

  if (loading) {
    return (
      <>
        {seo}
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Completing your registration...</h2>
            <p className="text-gray-600">Please wait while we set up your account.</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        {seo}
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="text-red-600 mb-4">
              <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Checkout Verification Failed</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Go to Login
              </button>
              <button
                onClick={() => navigate('/signup')}
                className="w-full bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Back to Sign Up
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return null;
};

export default CheckoutSuccessPage;

